import type { Issue } from "./types";
import { STATUSES, PRIORITIES } from "./constants";
import { useStore } from "./store";
import { formatDate } from "./utils";
import { humanOwnerId } from "./taskOwnership";

export function generateDocDraft(input: {
  title: string;
  projectName?: string;
  teamName?: string;
  issues: Issue[];
}) {
  const { title, projectName, teamName, issues } = input;
  const state = useStore.getState();
  const users = state.users;
  const labels = state.labels;

  const scope =
    projectName || teamName
      ? `${projectName ? `**Project**: ${projectName}` : ""}${
          projectName && teamName ? "  \n" : ""
        }${teamName ? `**Team**: ${teamName}` : ""}`
      : "**Scope**: workspace-level";

  const grouped = groupByStatus(issues);
  const open = issues.filter((i) => i.status !== "done" && i.status !== "canceled");
  const done = issues.filter((i) => i.status === "done");
  const urgent = issues.filter((i) => i.priority === 1 && i.status !== "done");

  const lines: string[] = [];
  lines.push(`# ${title}`);
  lines.push("");
  lines.push(
    `*Draft generated on ${new Date().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })} — refine before publishing.*`
  );
  lines.push("");
  lines.push("## Overview");
  lines.push("");
  lines.push(scope);
  lines.push("");
  lines.push(
    `Currently tracking **${issues.length} tasks** (${open.length} open, ${done.length} done).${
      urgent.length ? ` **${urgent.length} urgent.**` : ""
    }`
  );
  lines.push("");

  lines.push("## Current state");
  lines.push("");
  for (const statusGroup of STATUSES) {
    const group = grouped[statusGroup.id];
    if (!group?.length) continue;
    lines.push(`### ${statusGroup.name} · ${group.length}`);
    lines.push("");
    for (const iss of group.slice(0, 10)) {
      const assignee = users.find((u) => u.id === humanOwnerId(iss));
      const priority = PRIORITIES.find((p) => p.id === iss.priority);
      const tagNames = iss.labelIds
        .map((id) => labels.find((l) => l.id === id)?.name)
        .filter(Boolean);
      const bits = [
        `\`${iss.identifier}\``,
        iss.title,
        priority?.id ? `*${priority.name}*` : null,
        assignee ? `— @${assignee.name}` : null,
        tagNames.length ? `[${tagNames.join(", ")}]` : null,
      ].filter(Boolean);
      lines.push(`- ${bits.join(" ")}`);
    }
    if (group.length > 10) lines.push(`- _…and ${group.length - 10} more_`);
    lines.push("");
  }

  if (urgent.length) {
    lines.push("## Needs attention");
    lines.push("");
    for (const iss of urgent.slice(0, 5)) {
      lines.push(
        `- \`${iss.identifier}\` **${iss.title}** — ${
          STATUSES.find((s) => s.id === iss.status)?.name
        }${iss.dueDate ? ` · due ${formatDate(iss.dueDate)}` : ""}`
      );
    }
    lines.push("");
  }

  lines.push("## Open questions");
  lines.push("");
  lines.push("- _Fill these in when reviewing the draft._");
  lines.push("- ");
  lines.push("");

  lines.push("## Links");
  lines.push("");
  lines.push(`- [Workspace task board](#)`);
  if (projectName) lines.push(`- [Project: ${projectName}](#)`);

  return lines.join("\n");
}

export function generateProgressDraft(input: {
  title: string;
  projectName?: string;
  teamName?: string;
  issues: Issue[];
  periodStart: number;
  periodEnd: number;
}) {
  const { issues, periodStart, periodEnd, projectName, teamName } = input;

  const touched = issues.filter(
    (i) => i.updatedAt >= periodStart && i.updatedAt <= periodEnd
  );
  const completed = touched.filter((i) => i.status === "done");
  const started = touched.filter(
    (i) => i.status === "in_progress" || i.status === "in_review"
  );
  const blocked = touched.filter((i) => i.priority === 1 && i.status !== "done");

  const highlights: string[] = [];
  if (completed.length)
    highlights.push(`Shipped ${completed.length} task${completed.length > 1 ? "s" : ""}`);
  for (const iss of completed.slice(0, 3)) highlights.push(`${iss.title}`);

  const blockers: string[] = [];
  for (const iss of blocked.slice(0, 3)) blockers.push(`${iss.identifier}: ${iss.title}`);

  const nextSteps: string[] = [];
  for (const iss of started.slice(0, 3)) nextSteps.push(`Land ${iss.identifier}: ${iss.title}`);

  const summary = [
    projectName ? `Progress on **${projectName}**` : teamName ? `Progress on **${teamName}**` : "Weekly progress",
    ` this period: ${completed.length} shipped, ${started.length} in flight, ${
      blocked.length
    } urgent open. ${blocked.length ? "Focus next week: unblock the urgent set." : "No blockers flagged."}`,
  ].join("");

  const metrics = [
    { label: "Tasks shipped", value: String(completed.length) },
    { label: "In progress", value: String(started.length) },
    { label: "Urgent open", value: String(blocked.length) },
  ];

  return { summary, highlights, blockers, nextSteps, metrics, issueIds: touched.map((i) => i.id) };
}

function groupByStatus(issues: Issue[]) {
  const g: Record<string, Issue[]> = {};
  for (const s of STATUSES) g[s.id] = [];
  for (const i of issues) g[i.status].push(i);
  return g;
}
